import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getSyscoShopAuthToken } from '../../redux/slice/authShopSlice';
import { ApiClient } from '../utils/apiClient';
import { BFF_URL } from '../../constant/stringConstants';
import { buildEmptyCxml } from '../../constant/cxmlTemplates';
import Loading from '../loading/loading';
import ErrorPage from '../error/errorPage';
import './popupLanding.css';

const apiClient = new ApiClient(BFF_URL);

export default function PopupLanding() {
    const { orderReferenceId } = useParams();
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const popupRef = useRef(null);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingReturnData, setIsLoadingReturnData] = useState(false);
    const [returnError, setReturnError] = useState(null);

    const {
        isFetchTokenLoading,
        fetchTokenError,
        isFetchTokenSuccess,
        fetchTokenData
    } = useSelector(state => state.authShop);

    // Submit cXML to procurement system
    const submitToProcurement = useCallback((returnUrl, content) => {
        if (formRef.current && !isSubmitting) {
            setIsSubmitting(true);
            formRef.current.action = returnUrl;
            formRef.current.querySelector('input[name="cXML-urlencoded"]').value = content;
            formRef.current.submit();
        }
    }, [isSubmitting]);

    // Launch store in popup, or focus existing popup if already open
    const launchStore = useCallback(() => {
        if (!fetchTokenData) return;
        
        // If popup already exists and is open, just focus it
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.focus();
            return;
        }
        
        // Clear ref if popup was closed
        popupRef.current = null;
        
        const { authURL, token } = fetchTokenData;
        
        // Open new popup window
        const popup = window.open('about:blank', '_blank', 'width=1440,height=900,scrollbars=yes,resizable=yes');
        popupRef.current = popup;
        
        if (popup) {
            // Create and submit form in popup
            const form = popup.document.createElement('form');
            form.method = 'POST';
            form.action = authURL;
            
            const tokenInput = popup.document.createElement('input');
            tokenInput.type = 'hidden';
            tokenInput.name = 'token';
            tokenInput.value = token;
            form.appendChild(tokenInput);
            
            popup.document.body.appendChild(form);
            form.submit();
        } else {
            alert('Popup was blocked. Please allow popups for this site and try again.');
        }
    }, [fetchTokenData]);

    // Fetch auth credentials on mount
    useEffect(() => {
        dispatch(getSyscoShopAuthToken(orderReferenceId));
    }, [dispatch, orderReferenceId]);

    // Auto-launch store when auth credentials are ready
    useEffect(() => {
        if (isFetchTokenSuccess && fetchTokenData) {
            launchStore();
        }
    }, [isFetchTokenSuccess, fetchTokenData, launchStore]);

    // Listen for postMessage from popup
    useEffect(() => {
        const handleMessage = (event) => {
            // Validate message has required data
            if (event.data && event.data.returnUrl && event.data.content) {
                console.log('Received order data from popup');
                submitToProcurement(event.data.returnUrl, event.data.content);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [submitToProcurement]);

    // Return Items button handler - submits empty cXML to procurement system
    const handleReturnItems = useCallback(async () => {
        setIsLoadingReturnData(true);
        setReturnError(null);
        
        try {
            // Fetch buyerCookie and returnUrl from backend
            const response = await apiClient.get(`/order/return-info/${orderReferenceId}`);
            
            if (response.returnUrl && response.buyerCookie) {
                // Build empty cXML with zero total and submit to procurement system
                const emptyCxml = buildEmptyCxml(orderReferenceId, response.buyerCookie);
                submitToProcurement(response.returnUrl, emptyCxml);
            } else {
                setReturnError('Unable to fetch return information.');
            }
        } catch (error) {
            console.error('Error fetching return info:', error);
            setReturnError('Failed to fetch return data. Please try again.');
        } finally {
            setIsLoadingReturnData(false);
        }
    }, [orderReferenceId, submitToProcurement]);

    // Render loading state
    if (isFetchTokenLoading) {
        return <Loading />;
    }

    // Render error state
    if (fetchTokenError) {
        return <ErrorPage errorMessage={JSON.stringify(fetchTokenError)} />;
    }

    // Render submitting state
    if (isSubmitting) {
        return (
            <div className="popup-landing">
                <div className="modal">
                    <p>Submitting your order to procurement system...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="popup-landing">
            <div className="overlay" role="presentation"></div>
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                <p id="modal-title">Please keep this window open while shopping.</p>
                <br />
                
                <button 
                    className="btn btn-secondary" 
                    onClick={handleReturnItems}
                    disabled={isLoadingReturnData}
                >
                    {isLoadingReturnData ? 'Loading...' : 'Click here to return your items'}
                </button>
                
                {returnError && (
                    <p className="error-message">{returnError}</p>
                )}
                
                <br /><br />
                
                <span>
                    If the store did not launch,{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); launchStore(); }}>
                        click here
                    </a>.
                </span>
            </div>

            {/* Hidden form for submitting to procurement system */}
            <form
                ref={formRef}
                method="POST"
                target="_self"
                style={{ display: 'none' }}
            >
                <input type="hidden" name="cXML-urlencoded" value="" />
            </form>
        </div>
    );
}
